import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Search, RotateCcw, Download, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import DateRangePicker from '../components/DateRangePicker';
import { exportCsv } from '../utils/exportCsv';

export default function Completed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs,     setJobs]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [branches, setBranches] = useState([]);
  const [filters,  setFilters]  = useState({ branch: '', tech: '', search: '', result: '' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  useEffect(() => {
    api.get('/jobs/branches').then(r => setBranches(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, per_page: 50 };
    if (filters.branch) params.branch = filters.branch;
    if (filters.tech)   params.tech   = filters.tech;
    if (filters.search) params.search = filters.search;
    if (filters.result) params.result = filters.result;
    if (dateFrom)       params.date_from = dateFrom;
    if (dateTo)         params.date_to   = dateTo;
    api.get('/jobs/completed', { params })
      .then(r => { setJobs(r.data.jobs); setTotal(r.data.total); setPages(r.data.pages); })
      .finally(() => setLoading(false));
  }, [filters, page, dateFrom, dateTo]);

  function updateFilter(key, val) { setFilters(f => ({ ...f, [key]: val })); setPage(1); }

  function handleDateChange(from, to) { setDateFrom(from); setDateTo(to); setPage(1); }

  function clearAll() { setFilters({ branch: '', tech: '', search: '', result: '' }); setDateFrom(''); setDateTo(''); setPage(1); }

  async function deleteJob(job) {
    if (!window.confirm(`Permanently delete Job #${job.job_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      setJobs(j => j.filter(x => x.id !== job.id));
      setTotal(t => t - 1);
      toast.success(`Job #${job.job_number} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  }

  async function reopen(job) {
    try {
      await api.post(`/jobs/${job.id}/reopen`);
      toast.success(`Job #${job.job_number} reopened`);
      setJobs(j => j.filter(x => x.id !== job.id));
      setTotal(t => t - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reopen failed');
    }
  }

  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const params = { page: 1, per_page: 200 };
      if (filters.branch) params.branch = filters.branch;
      if (filters.tech)   params.tech   = filters.tech;
      if (filters.search) params.search = filters.search;
      if (filters.result) params.result = filters.result;
      if (dateFrom)       params.date_from = dateFrom;
      if (dateTo)         params.date_to   = dateTo;

      let all = [];
      let p = 1, totalPages = 1;
      do {
        const r = await api.get('/jobs/completed', { params: { ...params, page: p } });
        all = all.concat(r.data.jobs || []);
        totalPages = r.data.pages || 1;
        p++;
      } while (p <= totalPages);

      exportCsv('completed-audits.csv', all.map(j => ({
        'Job #': j.job_number, Branch: j.branch, Technician: j.primary_tech,
        'Job Type': j.job_type, 'Scheduled Date': j.scheduled_date?.slice(0,10),
        Customer: j.customer_name || '',
        Result: j.result || '', Score: j.score != null ? Math.round(j.score*100)+'%' : '',
        'Completed Date': j.completed_at?.slice(0,10) || '',
        'Completed By': j.completed_by_name || '',
        Notes: j.notes || '',
      })));
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Completed Audits</h1>
          <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} completed</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
          <button onClick={handleExport} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50">
            <Download size={13} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" placeholder="Search job, tech, customer..."
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
            />
          </div>
          <select value={filters.branch} onChange={e => updateFilter('branch', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]">
            <option value="">All Branches</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filters.result} onChange={e => updateFilter('result', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]">
            <option value="">All Results</option>
            <option value="Pass">Pass</option>
            <option value="Flag">Flag</option>
          </select>
          <button onClick={clearAll}
            className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            Clear All
          </button>
        </div>
      </div>

      {/* Table — grouped by branch → tech */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No completed audits found</div>
          ) : (() => {
            const branchMap = {};
            jobs.forEach(job => {
              const b = job.branch || 'Unknown';
              const t = job.primary_tech || 'Unknown';
              if (!branchMap[b]) branchMap[b] = {};
              if (!branchMap[b][t]) branchMap[b][t] = [];
              branchMap[b][t].push(job);
            });
            const COLS = ['Job #','Job Type','Date','Customer','Result','Score','Completed','Action'];
            return Object.entries(branchMap).map(([branch, techs]) => (
              <div key={branch}>
                <div className="bg-[#3D2B1A] text-white px-4 py-2 text-xs font-bold tracking-wider uppercase">
                  {branch}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {COLS.map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(techs).map(([tech, techJobs]) => {
                      const passed  = techJobs.filter(j => j.result === 'Pass').length;
                      const flagged = techJobs.filter(j => j.result === 'Flag').length;
                      return (
                        <>
                          <tr key={`tech-${tech}`} className="bg-gray-100 border-t border-b border-gray-200">
                            <td colSpan={COLS.length} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                              {tech}
                              <span className="ml-2 font-normal text-gray-400">{techJobs.length} job{techJobs.length !== 1 ? 's' : ''}</span>
                              {passed  > 0 && <span className="ml-3 text-green-600">{passed} Pass</span>}
                              {flagged > 0 && <span className="ml-2 text-red-600">{flagged} Flag</span>}
                            </td>
                          </tr>
                          {techJobs.map((job, i) => (
                            <tr key={job.id} className={`border-b border-gray-50 hover:bg-orange-50/40 cursor-pointer ${i%2===0?'bg-white':'bg-gray-50/30'}`}
                              onClick={() => navigate(`/audit/${job.id}`)}>
                              <td className="px-4 py-2.5 font-mono text-[#E8761A] text-xs">{job.job_number}</td>
                              <td className="px-4 py-2.5 text-gray-500 text-xs">{job.job_type}</td>
                              <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                                {job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 truncate max-w-[140px]">{job.customer_name || '—'}</td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  job.result === 'Pass' ? 'bg-green-100 text-green-700' :
                                  job.result === 'Flag' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-500'}`}>
                                  {job.result || '—'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {job.score != null
                                  ? <span className={`text-xs font-semibold ${job.score>=0.9?'text-green-600':job.score>=0.75?'text-amber-600':'text-red-600'}`}>
                                      {Math.round(job.score*100)}%
                                    </span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                                {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => reopen(job)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 text-gray-500 hover:text-amber-700">
                                    <RotateCcw size={11} /> Reopen
                                  </button>
                                  {user?.role === 'admin' && (
                                    <button onClick={() => deleteJob(job)} title="Delete job"
                                      className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ));
          })()}
        </div>

        {pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {pages}</span>
            <div className="flex gap-2">
              <button disabled={page===1} onClick={() => setPage(p=>p-1)} className="px-3 py-1 rounded border disabled:opacity-30 hover:bg-gray-50">Prev</button>
              <button disabled={page===pages} onClick={() => setPage(p=>p+1)} className="px-3 py-1 rounded border disabled:opacity-30 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
